<script setup>
definePage({
  meta: {
    action: 'read',
    subject: 'Med',
    navActiveLink: 'operational-infractions',
  },
})

import Breadcrumbs from '@/components/global/Breadcrumbs.vue'
import PageHeader from '@/components/global/PageHeader.vue'
import ActiveRulesTab from '@/components/operational/infractions/automation/ActiveRulesTab.vue'
import CreateAutomationRuleDialog from '@/components/operational/infractions/automation/CreateAutomationRuleDialog.vue'
import ExecutionLogsTab from '@/components/operational/infractions/automation/ExecutionLogsTab.vue'
import {
  extractAutomationExecutionsListPayload,
  isAutomationExecutionsListResponseOk,
  mapApiAutomationExecutionToRow,
} from '@/components/operational/infractions/automation/automationExecutionsMap'
import {
  extractAutomationRulesListPayload,
  isAutomationRulesListResponseOk,
  mapApiAutomationRuleToRow,
} from '@/components/operational/infractions/automation/automationRulesMap'
import { fetchMedAutomationExecutionsList, fetchMedAutomationRulesList } from '@/api'
import { PAGINATION_OPTIONS } from '@/utils/constants'

const router = useRouter()
const vm = getCurrentInstance()
const toast = useToast()

const tab = ref('active-rules')
const rulesTableLoading = ref(false)
const executionsTableLoading = ref(false)
const ruleItems = ref([])
const executionItems = ref([])
const rulesPageNum = ref(PAGINATION_OPTIONS.PAGE_NUM)
const rulesPageSize = ref(PAGINATION_OPTIONS.PAGE_SIZE)
const rulesTotal = ref(0)
const executionsPageNum = ref(PAGINATION_OPTIONS.PAGE_NUM)
const executionsPageSize = ref(PAGINATION_OPTIONS.PAGE_SIZE)
const executionsTotal = ref(0)
const isCreateRuleDialogVisible = ref(false)

const rulesPageCount = computed(() => {
  const totalPages = Math.ceil(rulesTotal.value / rulesPageSize.value)
  return totalPages > 0 ? totalPages : 1
})

const executionsPageCount = computed(() => {
  const totalPages = Math.ceil(executionsTotal.value / executionsPageSize.value)
  return totalPages > 0 ? totalPages : 1
})

const rulesPaginationLabel = computed(() => {
  if (!rulesTotal.value) {
    return vm.proxy.$t('infractionsAutomationModule.pagination.emptyRules')
  }

  const start = ((rulesPageNum.value - 1) * rulesPageSize.value) + 1
  const end = Math.min(rulesPageNum.value * rulesPageSize.value, rulesTotal.value)

  return vm.proxy.$t('infractionsAutomationModule.pagination.summaryRules', {
    start,
    end,
    total: rulesTotal.value,
  })
})

const executionsPaginationLabel = computed(() => {
  if (!executionsTotal.value) {
    return vm.proxy.$t('infractionsAutomationModule.pagination.emptyExecutions')
  }

  const start = ((executionsPageNum.value - 1) * executionsPageSize.value) + 1
  const end = Math.min(executionsPageNum.value * executionsPageSize.value, executionsTotal.value)

  return vm.proxy.$t('infractionsAutomationModule.pagination.summaryExecutions', {
    start,
    end,
    total: executionsTotal.value,
  })
})

const fetchRulesList = async () => {
  rulesTableLoading.value = true
  try {
    const res = await fetchMedAutomationRulesList({
      page: rulesPageNum.value,
      page_size: rulesPageSize.value,
    })

    if (!isAutomationRulesListResponseOk(res)) {
      toast.error(res?.message || vm.proxy.$t('infractionsAutomationModule.fetchFailed'))
      ruleItems.value = []
      rulesTotal.value = 0
      return
    }

    const { items, total } = extractAutomationRulesListPayload(res)
    ruleItems.value = (items || []).map(mapApiAutomationRuleToRow)
    rulesTotal.value = total
  } catch (error) {
    console.error('fetchMedAutomationRulesList failed', error)
    toast.error(vm.proxy.$t('infractionsAutomationModule.fetchError'))
    ruleItems.value = []
    rulesTotal.value = 0
  } finally {
    rulesTableLoading.value = false
  }
}

const fetchExecutionsList = async () => {
  executionsTableLoading.value = true
  try {
    const res = await fetchMedAutomationExecutionsList({
      page: executionsPageNum.value,
      page_size: executionsPageSize.value,
    })

    if (!isAutomationExecutionsListResponseOk(res)) {
      toast.error(res?.message || vm.proxy.$t('infractionsAutomationModule.executionLogsFetchFailed'))
      executionItems.value = []
      executionsTotal.value = 0
      return
    }

    const { items, total } = extractAutomationExecutionsListPayload(res)
    executionItems.value = (items || []).map(mapApiAutomationExecutionToRow)
    executionsTotal.value = total
  } catch (error) {
    console.error('fetchMedAutomationExecutionsList failed', error)
    toast.error(vm.proxy.$t('infractionsAutomationModule.executionLogsFetchError'))
    executionItems.value = []
    executionsTotal.value = 0
  } finally {
    executionsTableLoading.value = false
  }
}

const loadActiveTab = () => {
  if (tab.value === 'active-rules') {
    fetchRulesList()
    return
  }

  if (tab.value === 'execution-logs') {
    fetchExecutionsList()
  }
}

const goBackToInfractions = () => {
  router.push('/operational/infractions/1')
}

const handleCreateRule = () => {
  isCreateRuleDialogVisible.value = true
}

const handleCreateRuleSuccess = () => {
  rulesPageNum.value = PAGINATION_OPTIONS.PAGE_NUM
  if (tab.value === 'active-rules') {
    fetchRulesList()
  }
}

const onRulesPageChange = value => {
  rulesPageNum.value = value
  fetchRulesList()
}

const onRulesPageSizeChange = value => {
  rulesPageSize.value = Number(value)
  rulesPageNum.value = PAGINATION_OPTIONS.PAGE_NUM
  fetchRulesList()
}

const onExecutionsPageChange = value => {
  executionsPageNum.value = value
  fetchExecutionsList()
}

const onExecutionsPageSizeChange = value => {
  executionsPageSize.value = Number(value)
  executionsPageNum.value = PAGINATION_OPTIONS.PAGE_NUM
  fetchExecutionsList()
}

const handleRuleDeleted = () => {
  if (ruleItems.value.length === 1 && rulesPageNum.value > 1) {
    rulesPageNum.value -= 1
  }
  fetchRulesList()
}

watch(tab, () => {
  loadActiveTab()
})

onMounted(() => {
  loadActiveTab()
})
</script>

<template>
  <div class="infractions-automation-page">
    <Breadcrumbs
      class="mb-2"
      :items="[
        { title: $t('breadCrumbs.infractions'), to: '/operational/infractions/1' },
        { title: $t('breadCrumbs.infractionsAutomation'), disabled: true },
      ]"
    />
    <PageHeader
      :title="$t('pageHeaders.infractionsAutomation.title')"
      :subtitle="$t('pageHeaders.infractionsAutomation.subtitle')"
    />

    <VCard class="pa-5 mb-4" variant="flat" border>
      <div class="d-flex align-start justify-space-between flex-wrap ga-4 w-100 mb-4">
        <div class="d-flex flex-column" style="min-width: 0;">
          <h2 class="text-h5 mb-1">
            {{ $t('infractionsAutomationModule.title') }}
          </h2>
          <div class="text-body-2 text-medium-emphasis">
            {{ $t('infractionsAutomationModule.subtitle') }}
          </div>
        </div>

        <div class="d-flex align-center ga-2 flex-wrap" style="justify-content: flex-end;">
          <VBtn
            size="small"
            variant="outlined"
            color="default"
            @click="goBackToInfractions"
          >
            <VIcon
              icon="tabler-arrow-left"
              class="me-1"
              size="18"
            />
            {{ $t('infractionsAutomationModule.backToInfractions') }}
          </VBtn>
          <VBtn
            size="small"
            color="primary"
            @click="handleCreateRule"
          >
            <VIcon
              icon="tabler-plus"
              class="me-1"
              size="18"
            />
            {{ $t('infractionsAutomationModule.createRule') }}
          </VBtn>
        </div>
      </div>

      <VTabs v-model="tab">
        <VTab value="active-rules">
          {{ $t('infractionsAutomationModule.tabs.activeRules') }}
        </VTab>
        <VTab value="execution-logs">
          {{ $t('infractionsAutomationModule.tabs.executionLogs') }}
        </VTab>
      </VTabs>
    </VCard>

    <VWindow v-model="tab" :transition="false">
      <VWindowItem value="active-rules" eager>
        <ActiveRulesTab
          :items="ruleItems"
          :loading="rulesTableLoading"
          @status-changed="fetchRulesList"
          @rule-deleted="handleRuleDeleted"
        />

        <div class="d-flex flex-wrap justify-center justify-sm-space-between gap-y-2 px-5 py-3 mt-4">
          <div class="d-flex align-center justify-center flex-wrap gap-y-2">
            <span class="mr-2 text-body-2 text-medium-emphasis">{{ rulesPaginationLabel }}</span>
            <AppSelect
              :model-value="rulesPageSize"
              :items="PAGINATION_OPTIONS.ITEMS"
              density="compact"
              hide-details
              style="max-inline-size: 8rem; min-inline-size: 5rem;"
              @update:model-value="onRulesPageSizeChange"
            />
          </div>
          <VPagination
            :model-value="rulesPageNum"
            active-color="primary"
            :length="rulesPageCount"
            :total-visible="$vuetify.display.smAndDown ? 3 : 5"
            :size="$vuetify.display.smAndDown ? 36 : 'default'"
            @update:model-value="onRulesPageChange"
          />
        </div>
      </VWindowItem>

      <VWindowItem value="execution-logs" eager>
        <ExecutionLogsTab
          :items="executionItems"
          :loading="executionsTableLoading"
        />

        <div class="d-flex flex-wrap justify-center justify-sm-space-between gap-y-2 px-5 py-3 mt-4">
          <div class="d-flex align-center justify-center flex-wrap gap-y-2">
            <span class="mr-2 text-body-2 text-medium-emphasis">{{ executionsPaginationLabel }}</span>
            <AppSelect
              :model-value="executionsPageSize"
              :items="PAGINATION_OPTIONS.ITEMS"
              density="compact"
              hide-details
              style="max-inline-size: 8rem; min-inline-size: 5rem;"
              @update:model-value="onExecutionsPageSizeChange"
            />
          </div>
          <VPagination
            :model-value="executionsPageNum"
            active-color="primary"
            :length="executionsPageCount"
            :total-visible="$vuetify.display.smAndDown ? 3 : 5"
            :size="$vuetify.display.smAndDown ? 36 : 'default'"
            @update:model-value="onExecutionsPageChange"
          />
        </div>
      </VWindowItem>
    </VWindow>

    <CreateAutomationRuleDialog
      v-model:is-dialog-visible="isCreateRuleDialogVisible"
      @success="handleCreateRuleSuccess"
    />
  </div>
</template>
